<template>
  <div class="dashboard-index p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900">Dashboard</h1>
      <p class="text-gray-600 mt-1">Overview of your application statistics and recent activity</p>
    </div>

    <!-- Statistics Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <!-- Resumes -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Resumes</p>
            <p class="text-3xl font-bold text-gray-900">{{ total.resumes }}</p>
            <p class="text-xs text-green-600 mt-1">+{{ recent.resumes }} this week</p>
          </div>
          <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <icon name="file" class="w-6 h-6 text-blue-600" />
          </div>
        </div>
        <Link
          :href="route('admin.resumes.index')"
          class="mt-4 text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
        >
          View All
          <icon name="arrow-right" class="w-4 h-4" />
        </Link>
      </div>

      <!-- Lead Forms -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Lead Forms</p>
            <p class="text-3xl font-bold text-gray-900">{{ total.lead_forms }}</p>
            <p class="text-xs text-green-600 mt-1">+{{ recent.lead_forms }} this week</p>
          </div>
          <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <icon name="contact" class="w-6 h-6 text-green-600" />
          </div>
        </div>
        <Link
          :href="route('admin.lead-forms.index')"
          class="mt-4 text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
        >
          View All
          <icon name="arrow-right" class="w-4 h-4" />
        </Link>
      </div>

      <!-- Landing Pages -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Landing Pages</p>
            <p class="text-3xl font-bold text-gray-900">{{ total.landing_pages }}</p>
            <p class="text-xs text-green-600 mt-1">+{{ recent.landing_pages }} this week</p>
          </div>
          <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <icon name="page" class="w-6 h-6 text-purple-600" />
          </div>
        </div>
        <Link
          :href="route('admin.landing-pages.index')"
          class="mt-4 text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
        >
          View All
          <icon name="arrow-right" class="w-4 h-4" />
        </Link>
      </div>

      <!-- Link-in-Bio -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Link-in-Bio</p>
            <p class="text-3xl font-bold text-gray-900">{{ total.link_in_bio }}</p>
            <p class="text-xs text-green-600 mt-1">+{{ recent.link_in_bio }} this week</p>
          </div>
          <div class="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
            <icon name="website" class="w-6 h-6 text-pink-600" />
          </div>
        </div>
        <Link
          :href="route('admin.link-in-bio.index')"
          class="mt-4 text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
        >
          View All
          <icon name="arrow-right" class="w-4 h-4" />
        </Link>
      </div>

      <!-- Leads -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Leads</p>
            <p class="text-3xl font-bold text-gray-900">{{ total.leads }}</p>
            <p class="text-xs text-green-600 mt-1">+{{ recent.leads }} this week</p>
          </div>
          <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
            <icon name="contact" class="w-6 h-6 text-yellow-600" />
          </div>
        </div>
        <Link
          :href="route('admin.leads.index')"
          class="mt-4 text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
        >
          View All
          <icon name="arrow-right" class="w-4 h-4" />
        </Link>
      </div>

      <!-- Captured Leads -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Captured Leads</p>
            <p class="text-3xl font-bold text-gray-900">{{ total.captured_leads }}</p>
          </div>
          <div class="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
            <icon name="contact" class="w-6 h-6 text-indigo-600" />
          </div>
        </div>
      </div>

      <!-- Users -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Users</p>
            <p class="text-3xl font-bold text-gray-900">{{ total.users }}</p>
            <p class="text-xs text-green-600 mt-1">+{{ recent.users }} this week</p>
          </div>
          <div class="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <icon name="user" class="w-6 h-6 text-gray-600" />
          </div>
        </div>
        <Link
          :href="route('users')"
          class="mt-4 text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
        >
          View All
          <icon name="arrow-right" class="w-4 h-4" />
        </Link>
      </div>
    </div>

    <!-- Charts and Activity -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <!-- Visitor Chart -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">Visitor Statistics (Last 15 Days)</h2>
        <div class="h-64 flex items-end justify-between gap-1">
          <div
            v-for="(visit, index) in visitors"
            :key="index"
            class="flex-1 bg-green-500 rounded-t hover:bg-green-600 transition-colors"
            :style="{ height: `${Math.max((visit / Math.max(...visitors, 1)) * 100, 5)}%` }"
            :title="`${labels[index]}: ${visit} visits`"
          ></div>
        </div>
        <div class="mt-4 text-xs text-gray-500 text-center">
          <span v-for="(label, index) in labels" :key="index" class="inline-block mx-1">
            {{ index % 3 === 0 ? label.split(' ')[0] : '' }}
          </span>
        </div>
      </div>

      <!-- Most Active Users -->
      <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">Most Active Users</h2>
        <div v-if="activeUsers && activeUsers.length > 0" class="space-y-3">
          <div
            v-for="user in activeUsers"
            :key="user.id"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <div>
              <p class="font-medium text-gray-900">{{ user.name }}</p>
              <p class="text-sm text-gray-600">{{ user.email }}</p>
            </div>
            <div class="text-right">
              <p class="font-bold text-green-600">{{ user.total_content }}</p>
              <p class="text-xs text-gray-500">items</p>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-8 text-gray-500">
          <p>No active users yet</p>
        </div>
      </div>
    </div>

    <!-- Recent Leads -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-xl font-bold text-gray-900">Recent Leads</h2>
        <Link
          :href="route('admin.leads.index')"
          class="text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          View All
        </Link>
      </div>
      <div v-if="recentLeads && recentLeads.length > 0" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bio Page</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="lead in recentLeads" :key="lead.id" class="hover:bg-gray-50">
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ lead.created_at }}</td>
              <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ lead.name || 'N/A' }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                <a :href="'mailto:' + lead.email" class="text-green-600 hover:text-green-800 hover:underline">
                  {{ lead.email }}
                </a>
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                <span v-if="lead.profile">{{ lead.profile }}</span>
                <span v-else class="text-gray-400">N/A</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="text-center py-8 text-gray-500">
        <icon name="contact" class="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p>No recent leads</p>
      </div>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    total: {
      type: Object,
      required: true,
    },
    recent: {
      type: Object,
      required: true,
    },
    visitors: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    activeUsers: {
      type: Array,
      default: () => [],
    },
    recentLeads: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: 'Dashboard',
    },
  },
}
</script>

<style scoped>
.dashboard-index {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>



